import React from 'react';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="legal-page">
      <section className="section">
        <div className="container">
          <div className="legal-content">
            <h1>Terms of Service</h1>
            <p className="legal-effective-date"><strong>Effective Date:</strong> February 11, 2026</p>

            <p>
              These Terms of Service ("Terms") govern your use of the Inferium Reports mobile
              application and web platform (collectively, the "Service") operated by Inferium
              Reports ("we," "our," or "us"). By accessing or using the Service, you agree to
              be bound by these Terms.
            </p>

            <h2>1. Use of the Service</h2>
            <p>
              The Service is a business-to-business (B2B) security guard management and reporting
              platform. Access to the Service is provided through your employer or contracting
              organization ("your Organization"). You must use the Service in accordance with
              these Terms and any policies established by your Organization.
            </p>

            <h2>2. Accounts</h2>
            <p>
              Accounts are created and managed by authorized administrators within your Organization.
              You are responsible for maintaining the confidentiality of your login credentials and
              for all activities that occur under your account. You must notify your Organization's
              administrator immediately if you suspect unauthorized use of your account.
            </p>

            <h2>3. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose</li>
              <li>Share your account credentials with others</li>
              <li>Submit false or misleading information in reports</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Interfere with or disrupt the operation of the Service</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
              <li>Use the Service to collect information about other users without authorization</li>
            </ul>

            <h2>4. Content and Reports</h2>
            <p>
              You retain ownership of the content you submit through the Service, including
              report text and photographs. By submitting content, you grant your Organization
              and Inferium Reports a license to store, process, and display that content as
              necessary to provide the Service. Your Organization may retain reports and
              associated data in accordance with their own policies.
            </p>

            <h2>5. Location Services</h2>
            <p>
              The Service uses location data to verify guard presence at security sites, track
              patrol routes during active shifts, and attach location information to reports.
              By using the Service, you consent to the collection and use of your location
              data as described in our <a href="/privacy">Privacy Policy</a>. You may disable
              location services through your device settings, but this may limit the
              functionality of the Service.
            </p>

            <h2>6. SMS Notifications</h2>
            <p>
              Authorized administrators may send one-time SMS notifications to clients and property
              managers to deliver security report links. By providing a phone number to receive
              these notifications, recipients consent to receive transactional SMS messages from
              Inferium Reports via Twilio, Inc.
            </p>
            <ul>
              <li><strong>Message frequency:</strong> One message per report shared. No recurring or marketing messages will be sent.</li>
              <li><strong>Message and data rates may apply</strong> depending on your mobile carrier and plan.</li>
              <li><strong>To opt out:</strong> Reply <strong>STOP</strong> to any message at any time. You will receive a confirmation and no further messages will be sent to your number.</li>
              <li><strong>For help:</strong> Reply <strong>HELP</strong> or contact <a href="mailto:support@inferiumreports.com">support@inferiumreports.com</a>.</li>
            </ul>

            <h2>7. Intellectual Property</h2>
            <p>
              The Service, including its design, features, and underlying technology, is owned
              by Inferium Reports and protected by intellectual property laws. These Terms do
              not grant you any rights to our trademarks, logos, or other brand features.
            </p>

            <h2>8. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO
              NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
            </p>

            <h2>9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, INFERIUM REPORTS SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT
              OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE, WHETHER BASED ON WARRANTY,
              CONTRACT, TORT, OR ANY OTHER LEGAL THEORY.
            </p>

            <h2>10. Termination</h2>
            <p>
              Your access to the Service may be terminated by your Organization's administrator
              or by us at any time. Upon termination, your right to use the Service ceases
              immediately. Provisions of these Terms that by their nature should survive
              termination will remain in effect.
            </p>

            <h2>11. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. We will notify users of material changes
              by posting the updated Terms on this page and updating the effective date.
              Your continued use of the Service after changes constitutes acceptance of the
              modified Terms.
            </p>

            <h2>12. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the
              State of Washington, without regard to its conflict of law provisions. You
              consent to the exclusive jurisdiction of the state and federal courts located
              in Washington for any disputes arising out of or relating to these Terms or
              the Service.
            </p>

            <h2>13. Contact Us</h2>
            <p>
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <ul className="legal-contact-list">
              <li><strong>Email:</strong> <a href="mailto:support@inferiumreports.com">support@inferiumreports.com</a></li>
              <li><strong>Website:</strong> <a href="/contact">inferiumreports.com/contact</a></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
